import http from "@/services/http";
import {
  VehicleListParams,
  VehicleListItemDto,
  VehicleResponseDto,
  VehicleSpecResponseDto,
  CreateVehicleDto,
  UpdateVehicleDto,
  PagedResult,
} from "@/models/vehicle.model";
import {
  VehicleFieldChangeParams,
  VehicleFieldChangeResponse,
} from "@/models/vehicleFieldChange.model";

export interface VehicleListResponse {
  items: VehicleListItemDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

class VehicleService {
  // ==================== VEHICLES ====================

  async listVehicles(params: VehicleListParams = {}): Promise<VehicleListResponse> {
    const query = new URLSearchParams();
    const searchTerm = params.search?.trim();
    if (searchTerm) {
      query.set("search", searchTerm);
    }
    if (params.garageId) {
      query.set("garageId", params.garageId);
    }
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<
      PagedResult<VehicleListItemDto> | VehicleListItemDto[]
    >(`/vehicles${qs ? `?${qs}` : ""}`);

    const normalizeItems = (items: VehicleListItemDto[]) =>
      items.map((item: any) => ({
        ...item,
        dealerVehicleId: item.dealerVehicleId,
      }));

    // Manejo polimórfico (array vs paginado)
    if (Array.isArray(response)) {
      const items = normalizeItems(response);
      return {
        items,
        total: items.length,
        page: 1,
        pageSize: items.length,
        totalPages: 1,
      };
    }

    const items = normalizeItems(response.items || []);
    const totalFromResponse =
      typeof (response as any).totalCount === "number"
        ? (response as any).totalCount
        : typeof (response as any).total === "number"
          ? (response as any).total
          : typeof (response as any).totalItems === "number"
            ? (response as any).totalItems
            : undefined;
    const page =
      typeof (response as any).page === "number"
        ? (response as any).page
        : typeof (response as any).pageNumber === "number"
          ? (response as any).pageNumber
          : 1;
    const pageSize =
      typeof (response as any).pageSize === "number"
        ? (response as any).pageSize
        : typeof (response as any).page_size === "number"
          ? (response as any).page_size
          : items.length || 10;

    return {
      items,
      total: totalFromResponse ?? items.length,
      page,
      pageSize,
      totalPages: (response as any).totalPages || 1,
    };
  }

  async getVehicleById(dealerVehicleId: string): Promise<VehicleResponseDto> {
    return http.get<VehicleResponseDto>(`/vehicles/${dealerVehicleId}`);
  }

  async createVehicle(data: CreateVehicleDto): Promise<VehicleResponseDto> {
    return http.post<VehicleResponseDto>("/vehicles", data);
  }

  async updateVehicle(
    dealerVehicleId: string,
    data: UpdateVehicleDto
  ): Promise<VehicleResponseDto> {
    return http.put<VehicleResponseDto>(`/vehicles/${dealerVehicleId}`, data);
  }

  async deleteVehicle(dealerVehicleId: string): Promise<void> {
    await http.delete<void>(`/vehicles/${dealerVehicleId}`);
  }

  // ==================== SPECIAL ENDPOINTS ====================

  /**
   * Buscar specs por VIN para prellenar formulario.
   * No crea DealerVehicle.
   */
  async searchByVin(vin: string): Promise<VehicleSpecResponseDto> {
    return http.get<VehicleSpecResponseDto>(`/vehicles/vin/${vin}`);
  }

  /**
   * Forzar refresh de specs desde CarsXE.
   * No crea DealerVehicle.
   */
  async refreshFromApi(vin: string): Promise<VehicleSpecResponseDto> {
    return http.post<VehicleSpecResponseDto>(`/vehicles/vin/${vin}/refresh`, {});
  }

  // ==================== IMAGES ====================

  async uploadImage(dealerVehicleId: string, file: File): Promise<VehicleResponseDto> {
    const formData = new FormData();
    formData.append('file', file);
    return http.postFormData<VehicleResponseDto>(`/vehicles/${dealerVehicleId}/images`, formData);
  }

  async deleteImage(dealerVehicleId: string, vehicleImageId: string): Promise<void> {
    await http.delete<void>(`/vehicles/${dealerVehicleId}/images/${vehicleImageId}`);
  }

  // ==================== FIELD CHANGE HISTORY ====================

  async getFieldChanges(
    dealerVehicleId: string,
    params: VehicleFieldChangeParams = {}
  ): Promise<VehicleFieldChangeResponse> {
    const query = new URLSearchParams();
    if (params.fieldName) query.set("fieldName", params.fieldName);
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();
    return http.get<VehicleFieldChangeResponse>(
      `/vehicles/${dealerVehicleId}/field-changes${qs ? `?${qs}` : ""}`
    );
  }
}

export default new VehicleService();
